import { createContext } from "react";

import { Consult } from "@/types/care";
import { TreatmentCategories } from "@/utils/treatmentUtils";

export type ConsultComputations = {
  // Status-based computations
  openConsults: Consult[];
  completedConsults: Consult[];

  // Type-based computations
  vaginitisOrUngatedRxConsults: Consult[];
  stiConsults: Consult[];
  malePartnerConsults: Consult[];
  openUngatedRxConsults: Consult[];

  // Most recent/specific consults
  mostRecentVaginitisOrUngatedRxConsult: Consult | undefined;
  firstSTIConsult: Consult | undefined;
  mostRecentCompletedVaginitisOrUngatedRxConsult: Consult | undefined;
  firstConsultWithRefills: Consult | undefined;
  latestConsult: Consult | undefined;
  latestPaidConsult: Consult | undefined;
  latestPreSubmitConsult: Consult | undefined;
  latestConsultInProgress: Consult | undefined;

  // Past treatments/messages computations
  hasPastConsultMessages: boolean;
  hasPastTreatments: boolean;
};

export type ConsultsContextType = {
  error?: string;
  consults?: Consult[];
  setConsults: (consults: Consult[]) => void;
  loading: boolean;
  refetchConsults: () => void;
  consultComputations: ConsultComputations;
  // Treatment processing methods
  getTreatmentsForConsult: (consultId: string) => TreatmentCategories;
  findPrescriptionBySlug: (consultId: string, slug: string) => any;
};

const defaultComputations: ConsultComputations = {
  openConsults: [],
  completedConsults: [],
  vaginitisOrUngatedRxConsults: [],
  stiConsults: [],
  malePartnerConsults: [],
  openUngatedRxConsults: [],
  mostRecentVaginitisOrUngatedRxConsult: undefined,
  firstSTIConsult: undefined,
  mostRecentCompletedVaginitisOrUngatedRxConsult: undefined,
  firstConsultWithRefills: undefined,
  hasPastConsultMessages: false,
  hasPastTreatments: false,
  latestPreSubmitConsult: undefined,
  latestConsult: undefined,
  latestPaidConsult: undefined,
  latestConsultInProgress: undefined,
};

export const ConsultsContext = createContext<ConsultsContextType>({
  error: "",
  consults: [],
  setConsults: () => {},
  loading: false,
  refetchConsults: () => {},
  consultComputations: defaultComputations,
  // Default treatment processing methods
  getTreatmentsForConsult: () => ({
    allPrescriptions: [],
    prescriptionsWithNoRefillsRemaining: [],
    prescriptionsWithRefillsRemaining: [],
    prescriptionsNotRefillable: [],
    alwaysRefillableTreatments: [],
  }),
  findPrescriptionBySlug: () => undefined,
});
